import React, { useState, useEffect, useCallback } from "react";
import "./CourtsNew.css";
import AddCourtButton from "./components/AddCourtButton.js";
import Map from "./Map.js";
import CourtItem from "./CourtItem.js";
import { useParams, useLocation } from "react-router-dom";
import { getCoordinatesFromPostcode, calculateDistance } from "./utils.js";
import CountryInput from "./components/CountryInput.js";

function CourtsNew() {
  const { country, state, city } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialPostcode = queryParams.get('postcode') || '';

  // const [postcode, setPostcode] = useState(initialPostcode);
  const [postcode] = useState(initialPostcode);
  const [allCourts, setAllCourts] = useState([]);
  const [filteredCourts, setFilteredCourts] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState({});
  const [viewType, setViewType] = useState("list");
  const [radius, setRadius] = useState(10);
  const [sortBy] = useState("distance");
  // const [sortBy, setSortBy] = useState("distance");
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  const isValidPostcode = (postcode) => {
    const postcodeRegex = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i;
    return postcodeRegex.test(postcode);
  };

  const capitalizeWords = (text) =>
    text.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  const getHeading = () => {
    if (country && !state) {
      return `Futsal Courts in ${country.startsWith("united-") ? "the " : ""}${capitalizeWords(country)}`;
    }
    if (country && state && !city) {
      return `Futsal Courts in ${capitalizeWords(state)}`;
    }
    if (country && state && city) {
      return `Futsal Courts in ${capitalizeWords(city)}`;
    }
    return "Futsal Courts";
  };

  const [filters, setFilters] = useState({
    country: country,
    state: state,
    city: city,
    has_parking: false,
    has_cafe: false,
    has_seating: false,
    has_vending: false,
    has_disability_access: false,
    has_changing_rooms: false,
    has_showers: false,
    has_toilets: false,
    has_gym: false,
    has_fitness_studio: false,
    has_community_room: false,
  });

const handleFilterChange = (arg1, arg2) => {
  // Case 1: Called with event
  if (arg1 && arg1.target) {
    const { name, value } = arg1.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  }
  // Case 2: Called with (name, value) directly
  else {
    const name = arg1;
    const value = arg2;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  }
};

  const formatLabel = (key) => {
    return key
      .replace(/^has_/, '') // Remove 'has_' prefix
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
  };

  // Facility filters
  const facilityKeys = [
    "has_parking",
    "has_cafe",
    "has_seating",
    "has_vending",
    "has_disability_access",
    "has_changing_rooms",
    "has_showers",
    "has_toilets",
    "has_gym",
    "has_fitness_studio",
    "has_community_room",
  ];

  const fetchCourts = useCallback(async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams();

      // Location filters
      if (filters.country) queryParams.append('country', filters.country);
      if (filters.state) queryParams.append('state', filters.state);
      if (filters.city) queryParams.append('city', filters.city);

      // Facility filters
      const facilityKeys = [
        "has_parking",
        "has_cafe",
        "has_seating",
        "has_vending",
        "has_disability_access",
        "has_changing_rooms",
        "has_showers",
        "has_toilets",
        "has_gym",
        "has_fitness_studio",
        "has_community_room",
      ];

      facilityKeys.forEach((key) => {
        if (filters[key]) queryParams.append(key, true);
      });

      const response = await fetch(
        `${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/venues?${queryParams.toString()}`
      );
      const data = await response.json();

      const normalized = data.map((venue) => ({
        id: venue.id,
        name: venue.name,
        slug: venue.slug,
        description: venue.description,
        coordinates: [venue.latitude, venue.longitude],
        photos: venue.images || [],
        courts: venue.courts || [],
        contact_email: venue.contact_email,
        contact_phone: venue.contact_phone,
        website: venue.website,
        address: {
          firstLine: venue.address_line1,
          secondLine: venue.address_line2,
          city: venue.city,
          state: venue.state,
          postcode: venue.postcode,
          country: venue.country,
        },
        has_parking: venue.has_parking,
        has_cafe: venue.has_cafe,
        has_seating: venue.has_seating,
        has_vending: venue.has_vending,
        has_disability_access: venue.has_disability_access,
        has_changing_rooms: venue.has_changing_rooms,
        has_showers: venue.has_showers,
        has_toilets: venue.has_toilets,
        has_gym: venue.has_gym,
        has_fitness_studio: venue.has_fitness_studio,
        has_community_room: venue.has_community_room,
        is_verified: venue.is_verified,
      }));

      setAllCourts(normalized);
    } catch (error) {
      console.error('Error fetching courts:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCourts();
  }, [fetchCourts]);

  const applyFilters = useCallback(
    async (courts) => {
      if (isValidPostcode(postcode)) {
        try {
          const coordinates = await getCoordinatesFromPostcode(postcode);

          const courtsWithDistances = courts.map((court) => {
            const distance = calculateDistance(
              coordinates.lat,
              coordinates.lng,
              court.coordinates[0],
              court.coordinates[1]
            );
            return { ...court, distance };
          });

          let filtered = courtsWithDistances.filter(
            (court) => court.distance <= radius
          );

          if (sortBy === "distance") {
            filtered.sort((a, b) => a.distance - b.distance);
          } else if (sortBy === "numberOfCourts") {
            filtered.sort((a, b) => b.courts.length - a.courts.length);
          }

          setFilteredCourts(filtered);
          setCurrentPage(1);
        } catch (error) {
          console.error("Error fetching coordinates:", error);
          setFilteredCourts([]);
        }
      } else if (postcode === "") {
        setFilteredCourts(courts);
      } else {
        setFilteredCourts([]);
      }
    },
    [postcode, radius, sortBy]
  );

  useEffect(() => {
    fetchCourts();
  }, [fetchCourts]);

  useEffect(() => {
    applyFilters(allCourts);
  }, [postcode, radius, sortBy, allCourts, applyFilters]);

  // const handlePostcodeChange = (e) => {
  //   const value = e.target.value.toUpperCase();
  //   setPostcode(value);
  // };

  const handleNextPhoto = (index) => {
    setCurrentPhotoIndex((prev) => ({
      ...prev,
      [index]: ((prev[index] || 0) + 1) % filteredCourts[index].photos.length,
    }));
  };

  const handlePrevPhoto = (index) => {
    setCurrentPhotoIndex((prev) => ({
      ...prev,
      [index]: ((prev[index] || 0) - 1 + filteredCourts[index].photos.length) % filteredCourts[index].photos.length,
    }));
  };

  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const currentResults = filteredCourts.slice(indexOfFirstResult, indexOfLastResult);

  return (
    <div className="courts-page">
      <h1 className="courts-heading">{getHeading()}</h1>
      <div className="postcode-input-container">
        <div className="country-input-left">
          <label htmlFor="country"></label>
          <CountryInput
            filters={filters}
            handleFilterChange={handleFilterChange}
            fetchCourts={fetchCourts}
          />        </div>
        {/* <div className="postcode-input-left">
          <label htmlFor="postcode">Enter your postcode:</label>
          <input
            type="text"
            id="postcode"
            value={postcode}
            onChange={handlePostcodeChange}
            placeholder="e.g. SW19 5AG"
          />
          {!isValidPostcode(postcode) && postcode && (
            <p className="error-message">⚠️ Invalid Postcode</p>
          )}
          <button disabled={!isValidPostcode(postcode)}>Search</button>
        </div> */}
        {/* <FacilitiesDropdown
          facilityKeys={facilityKeys}
          filters={filters}
          handleFilterChange={handleFilterChange}
          formatLabel={formatLabel}
        /> */}
        <div className="checkbox-grid">
          <label className="facilities-label">Facilities</label>
          {facilityKeys.map((key) => (
            <label key={key} style={{ display: 'block', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={filters[key]}
                onChange={(e) => handleFilterChange(key, e.target.checked)}
              />
              {" "}
              {formatLabel(key)}
            </label>
          ))}
        </div>
        <div className="radius-dropdown">
          <label htmlFor="radius">Search radius (KM):</label>
          <select
            id="radius"
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
          >
            <option value={5}>5 KM</option>
            <option value={10}>10 KM</option>
            <option value={20}>20 KM</option>
            <option value={50}>50 KM</option>
          </select>
        </div>
        {/* <div className="sort-dropdown">
          <label htmlFor="sort">Sort by:</label>
          <select
            id="sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="distance">Distance (Closest First)</option>
            <option value="numberOfCourts">Number of Courts</option>
          </select>
        </div> */}
      </div>

      {isValidPostcode(postcode) && (
        <p className="postcode-message">
          Showing courts within <strong>{radius}KM</strong> of <strong>{postcode.toUpperCase()}</strong>
        </p>
      )}

      <div className="view-toggle">
        <button
          className={viewType === "list" ? "active" : ""}
          onClick={() => setViewType("list")}
        >
          LIST VIEW
        </button>
        <button
          className={viewType === "map" ? "active" : ""}
          onClick={() => setViewType("map")}
        >
          MAP VIEW
        </button>
      </div>

      {loading ? (
        <p>Loading courts...</p>
      ) : viewType === "list" ? (
        <div className="courts-list">
          {currentResults.map((court, index) => (
            <CourtItem
              key={index}
              court={court}
              index={index}
              currentPhotoIndex={currentPhotoIndex}
              handlePrevPhoto={handlePrevPhoto}
              handleNextPhoto={handleNextPhoto}
            />
          ))}
          <div className="pagination-controls">
            <div className="pagination-center">
              <button
                className="arrow-button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                &larr;
              </button>
              <span className="page-number">Page {currentPage}</span>
              <button
                className="arrow-button"
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={indexOfLastResult >= filteredCourts.length}
              >
                &rarr;
              </button>
            </div>
            <div className="results-per-page-dropdown">
              <label htmlFor="resultsPerPage">Results per page:</label>
              <select
                id="resultsPerPage"
                value={resultsPerPage}
                onChange={(e) => {
                  setResultsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>
      ) : (
        <Map courts={filteredCourts} />
      )}

      <div className="end-of-list-add-court-text">
        <text>Can't see a court that should be on here? Submit it here 👇</text>
      </div>
      <div className="end-of-list-add-court-button">
        <AddCourtButton />
      </div>
    </div>
  );
}

export default CourtsNew;
